<script setup lang="ts">
import { onBeforeUnmount, useId } from "vue";
defineOptions({ name: "BaseTextbox" });
const model = defineModel<string | undefined>();
const props = defineProps<{
  placeholder: string;
  label?: string;
  valueTransformerDelay?: number;
  valueTransformer?: (value: string | undefined) => string | undefined;
}>();
const inputId = useId();

let transformTimeout: ReturnType<typeof setTimeout> | undefined;

const clearTransformTimeout = () => {
  if (transformTimeout) {
    clearTimeout(transformTimeout);
    transformTimeout = undefined;
  }
};

const applyValueTransformer = () => {
  if (!props.valueTransformer) {
    return;
  }

  const transformedValue = props.valueTransformer(model.value);
  if (transformedValue !== model.value) {
    model.value = transformedValue;
  }
};

const handleInput = (event: Event) => {
  const target = event.target as HTMLInputElement;
  model.value = target.value;

  clearTransformTimeout();

  if (
    props.valueTransformer &&
    typeof props.valueTransformerDelay === "number" &&
    props.valueTransformerDelay >= 0
  ) {
    transformTimeout = setTimeout(() => {
      transformTimeout = undefined;
      applyValueTransformer();
    }, props.valueTransformerDelay);
  }
};

const handleBlur = () => {
  clearTransformTimeout();

  if (props.valueTransformer) {
    applyValueTransformer();
  }
};

onBeforeUnmount(() => {
  clearTransformTimeout();
});
</script>
<template>
  <input
    :id="inputId"
    type="text"
    class="textbox"
    :placeholder="props.placeholder"
    :value="model"
    :aria-label="props.label || props.placeholder"
    @input="handleInput"
    @blur="handleBlur"
  />
</template>
